import {
  Blocks,
  Bot,
  BriefcaseBusiness,
  Clapperboard,
  ExternalLink,
  Film,
  Fingerprint,
  Gamepad2,
  Globe2,
  Layers3,
  Palette,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from 'lucide-react';

// Stable personal details and contact links live in one auditable place.
export const profile = {
  name: 'Afshin Zehtab',
  fullName: 'Amirhossein Zehtabian',
  location: 'Munich, Germany',
  email: 'afshinzehtab@yahoo.com',
  github: 'https://github.com/afshinZehtab',
  linkedin: 'https://linkedin.com/in/afshinzehtab',
  site: 'https://afshinzehtab.com',
};

// Portfolio cards combine real destinations with lightweight visual identities.
export const projects = [
  {
    id: 'codinat',
    index: '01',
    title: 'Codinat EMS',
    url: 'https://codinat.de/',
    domain: 'codinat.de',
    year: '2026',
    type: 'Product / SaaS',
    icon: UsersRound,
    accent: '#ef1b2d',
    stack: ['Next.js', 'Strapi', 'Tailwind', 'MySQL'],
    description: {
      en: 'A focused employee-management workspace designed to make HR operations clearer, faster and easier to scale.',
      de: 'Ein fokussierter Workspace für Personalmanagement, der HR-Abläufe klarer, schneller und skalierbarer macht.',
      fa: 'فضایی یکپارچه برای مدیریت کارکنان که فرایندهای منابع انسانی را شفاف‌تر، سریع‌تر و مقیاس‌پذیرتر می‌کند.',
    },
  },
  {
    id: 'm8',
    index: '02',
    title: 'M8 Security',
    url: 'https://m8security.de/',
    domain: 'm8security.de',
    year: '2025',
    type: 'Corporate / Security',
    icon: ShieldCheck,
    accent: '#ff3b30',
    stack: ['React', 'UX Strategy', 'Responsive UI'],
    description: {
      en: 'A confident corporate presence for a security brand, balancing trust, clarity and fast access to essential services.',
      de: 'Ein souveräner Unternehmensauftritt für eine Sicherheitsmarke – mit Vertrauen, Klarheit und schnellem Servicezugang.',
      fa: 'یک حضور دیجیتال مطمئن برای برند امنیتی با تمرکز بر اعتماد، شفافیت و دسترسی سریع به خدمات اصلی.',
    },
  },
  {
    id: 'one-euro',
    index: '03',
    title: 'One Euro Link',
    url: 'https://oneeurolink.codinat.de/',
    domain: 'oneeurolink.codinat.de',
    year: '2026',
    type: 'Web Application',
    icon: Globe2,
    accent: '#ef1b2d',
    stack: ['Node.js', 'Product Design', 'API'],
    description: {
      en: 'A streamlined digital product shaped around a memorable concept, direct interaction and a frictionless user journey.',
      de: 'Ein schlankes digitales Produkt mit einprägsamem Konzept, direkter Interaktion und reibungsloser User Journey.',
      fa: 'یک محصول دیجیتال ساده و به‌یادماندنی با تعامل مستقیم و مسیر کاربری بدون اصطکاک.',
    },
  },
  {
    id: 'irankino',
    index: '04',
    title: 'IranKino',
    url: 'https://irankino.com/',
    domain: 'irankino.com',
    year: '2025',
    type: 'Streaming Platform',
    icon: Film,
    accent: '#e50914',
    stack: ['React', 'Content Platform', 'Streaming UI'],
    description: {
      en: 'A content-rich streaming experience for Iranian and international cinema, built for discovery across every screen.',
      de: 'Ein inhaltsreiches Streaming-Erlebnis für iranisches und internationales Kino – optimiert für jeden Bildschirm.',
      fa: 'تجربه‌ای غنی برای تماشای سینمای ایران و جهان که برای کشف محتوا در هر نمایشگری طراحی شده است.',
    },
  },
  {
    id: 'irankino-bot',
    index: '05',
    title: 'IranKino VPN',
    url: 'https://bot.irankino.com/',
    domain: 'bot.irankino.com',
    year: '2025',
    type: 'Automation / Bot',
    icon: Bot,
    accent: '#ef1b2d',
    stack: ['Automation', 'Node.js', 'API Integration'],
    description: {
      en: 'A companion automation experience that shortens the path between a viewer, a search and the right piece of content.',
      de: 'Eine begleitende Automation, die den Weg von der Suche zum passenden Inhalt deutlich verkürzt.',
      fa: 'یک تجربه‌ی خودکار و همراه که فاصله‌ی میان جست‌وجوی کاربر و محتوای مناسب را کوتاه می‌کند.',
    },
  },
  {
    id: 'spy',
    title: {
      en: 'Spy Game',
      de: 'Spy Game',
      fa: 'اسپای گیم',
    },
    url: 'https://spy.codinat.de',
    domain: 'spy.codinat.de',
    year: '2026',
    type: {
      en: 'Multiplayer / Web App',
      de: 'Multiplayer / Web-App',
      fa: 'چندنفره / وب‌اپ',
    },
    icon: Gamepad2,
    accent: '#ef1b2d',
    stack: ['Next.js', 'FastAPI', 'PostgreSQL', 'Redis'],
    cta: {
      en: 'Open project',
      de: 'Projekt öffnen',
      fa: 'مشاهده پروژه',
    },
    description: {
      en: 'A real-time multiplayer social deduction game with private rooms, hidden roles, voice chat, voting, scoring and Telegram integration.',
      de: 'Ein Echtzeit-Multiplayer-Social-Deduction-Spiel mit privaten Räumen, geheimen Rollen, Voice-Chat, Abstimmungen, Punktesystem und Telegram-Integration.',
      fa: 'یک بازی چندنفره بلادرنگ با اتاق‌های خصوصی، نقش‌های مخفی، گفت‌وگوی صوتی، رأی‌گیری، امتیازدهی و یکپارچه‌سازی با تلگرام.',
    },
  },
];

// Career entries preserve the facts from the previous resume page.
export const experience = [
  {
    period: '01.2025 — NOW',
    company: 'Jonas Better Place GmbH',
    role: {
      en: 'Facility Manager · Refugee shelter',
      de: 'Einrichtungsleitung · Flüchtlingsunterkunft',
      fa: 'مدیر مجموعه · پناهگاه پناهندگان',
    },
    detail: {
      en: 'Leading operations, external representation, people management and cross-team coordination.',
      de: 'Leitung des Betriebs, externe Vertretung, Personalmanagement und teamübergreifende Koordination.',
      fa: 'هدایت عملیات، نمایندگی بیرونی، مدیریت نیروها و هماهنگی میان تیم‌ها.',
    },
  },
  {
    period: '11.2022 — 12.2024',
    company: 'Jonas Better Place GmbH',
    role: {
      en: 'Management / Administration · Refugee shelter',
      de: 'Leitung / Verwaltung · Flüchtlingsunterkunft',
      fa: 'مدیریت و امور اداری · پناهگاه پناهندگان',
    },
    detail: {
      en: 'Coordinated administrative workflows and day-to-day operations in a people-focused environment.',
      de: 'Koordination administrativer Abläufe und des Tagesgeschäfts in einem menschenorientierten Umfeld.',
      fa: 'هماهنگی فرایندهای اداری و عملیات روزمره در محیطی انسان‌محور.',
    },
  },
  {
    period: '07.2021 — 11.2022',
    company: 'Jonas Better Place GmbH',
    role: { en: 'Social Worker', de: 'Sozialbetreuer', fa: 'مددکار اجتماعی' },
    detail: {
      en: 'Supported residents and connected complex needs with practical, respectful solutions.',
      de: 'Unterstützung von Bewohnern und Überführung komplexer Bedürfnisse in respektvolle, praktische Lösungen.',
      fa: 'حمایت از ساکنان و تبدیل نیازهای پیچیده به راه‌حل‌های عملی و محترمانه.',
    },
  },
  {
    period: '11.2020 — 06.2021',
    company: 'PulsM GmbH',
    role: { en: 'Deputy Facility Manager', de: 'Stellv. Objektleitung', fa: 'جانشین مدیر مجموعه' },
    detail: {
      en: 'Assisted with team leadership, site organisation and reliable daily delivery.',
      de: 'Unterstützung bei Teamführung, Standortorganisation und zuverlässiger Leistungserbringung.',
      fa: 'همکاری در رهبری تیم، سازمان‌دهی مجموعه و اجرای دقیق امور روزانه.',
    },
  },
  {
    period: '2019',
    company: 'IDESIA GmbH',
    role: { en: 'Full-Stack Web Developer', de: 'Full-Stack-Webentwickler', fa: 'توسعه‌دهنده فول‌استک وب' },
    detail: {
      en: 'Built full-stack solutions and SQL/NoSQL data structures to improve business processes.',
      de: 'Entwicklung von Full-Stack-Lösungen und SQL/NoSQL-Datenstrukturen zur Optimierung von Geschäftsprozessen.',
      fa: 'توسعه راهکارهای فول‌استک و ساختارهای داده SQL/NoSQL برای بهبود فرایندهای کسب‌وکار.',
    },
  },
  {
    period: '2017 — 2019',
    company: 'Visible Bytes GmbH',
    role: { en: 'Web Developer', de: 'Webentwickler', fa: 'توسعه‌دهنده وب' },
    detail: {
      en: 'Created modern frontend interfaces and translated UI/UX concepts into fast, usable products.',
      de: 'Entwicklung moderner Frontends und Umsetzung von UI/UX-Konzepten in schnelle, nutzbare Produkte.',
      fa: 'ساخت رابط‌های مدرن و تبدیل ایده‌های UI/UX به محصولات سریع و کاربردی.',
    },
  },
  {
    period: '2015 — 2017',
    company: 'SixReasons GmbH',
    role: { en: 'Web Developer', de: 'Webentwickler', fa: 'توسعه‌دهنده وب' },
    detail: {
      en: 'Developed custom websites and CMS solutions for efficient digital content management.',
      de: 'Entwicklung individueller Websites und CMS-Lösungen für effizientes digitales Content-Management.',
      fa: 'توسعه وب‌سایت‌ها و راهکارهای CMS سفارشی برای مدیریت مؤثر محتوای دیجیتال.',
    },
  },
];

// Skill groups intentionally mix engineering, product thinking and visual craft.
export const skillGroups = [
  {
    key: 'frontend',
    icon: Layers3,
    skills: ['React', 'Vue.js', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Sass'],
  },
  {
    key: 'backend',
    icon: Blocks,
    skills: ['Node.js', 'PHP', 'Python', 'MySQL', 'NoSQL', 'Strapi'],
  },
  {
    key: 'product',
    icon: Palette,
    skills: ['UI/UX Design', 'Design Systems', 'Responsive Design', 'Figma', 'Accessibility'],
  },
  {
    key: 'tooling',
    icon: Fingerprint,
    skills: ['Git', 'Docker', 'REST APIs', 'SEO', 'Performance', 'Automation'],
  },
];

export const education = [
  {
    icon: Sparkles,
    year: '2019',
    place: 'Munich',
    title: { en: 'Digital Media Designer (IHK)', de: 'Mediengestalter Digital (IHK)', fa: 'طراح رسانه‌های دیجیتال (IHK)' },
  },
  {
    icon: BriefcaseBusiness,
    year: '3 YEARS',
    place: 'Iran',
    title: { en: 'Electrical Engineering', de: 'Elektrotechnik', fa: 'مهندسی برق' },
  },
];

export const languages = [
  { level: 100, label: { en: 'Persian · Native', de: 'Persisch · Muttersprache', fa: 'پارسی · زبان مادری' } },
  { level: 80, label: { en: 'German · B2', de: 'Deutsch · B2', fa: 'آلمانی · B2' } },
  { level: 75, label: { en: 'English · B2', de: 'Englisch · B2', fa: 'انگلیسی · B2' } },
];

// Small icon pool is used by the horizontal craft ticker.
export const craftIcons = [Clapperboard, Globe2, ExternalLink, Palette];
